import { useState, useCallback, useMemo, memo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { css } from 'styled-components';
import { useQuery } from '@apollo/client';
import type { ColumnDef } from '@tanstack/react-table';
import AddCircle from '@mui/icons-material/AddCircle';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SearchIcon from '@mui/icons-material/Search';
import Tooltip from 'common/ui/Tooltip';
import Dialog from '@mui/material/Dialog';
import { Stack } from 'common/ui/Layout';
import GenericSnackBar from 'common/GenericSnackBar/GenericSnackBar';

import { Button } from 'common/ui/Button';
import { Colors, Spacings } from 'styles';
import Text from 'common/ui/Text';
import Input from 'common/ui/Input';
import { GET_ALL_HANGING_PROTOCOLS } from 'modules/Apollo/queries';

import { Table } from 'common/Table';
import { toLocaleString } from 'utils/date';
import { DraggableContainer } from 'domains/viewer/DraggableContainer';
import {
  useHangingProtocol,
  invalidateHPInitializationCache,
  invalidateHPSmidGuard,
  markHangingProtocolDeleted,
  broadcastHPInvalidation,
} from 'domains/viewer/HangingProtocol/useHangingProtocol';
import {
  usePreviewHangingProtocol,
  usePreviewHangingProtocolConfiguration,
} from 'domains/viewer/HangingProtocol/usePreviewHangingProtocol';
import { useSelectedHangingProtocolSmid } from 'domains/viewer/HangingProtocol/useSelectedHangingProtocol';
import type { Layouts } from '../../StudyLoader/viewerLoaderState';
import type {
  GetAllHangingProtocolsQuery,
  GetAllHangingProtocolsQueryVariables,
  GetHangingProtocolMatchQuery,
} from 'generated/graphql';
import { useOnUnload } from 'hooks/useOnUnload';
import { BaseWidget, AccessoryButton } from '../BaseWidget';
import { Menu, MenuItem } from 'common/ui/Menu';
import MoreVert from '@mui/icons-material/MoreVert';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import { logger } from 'modules/logger';
import BorderColor from '@mui/icons-material/BorderColor';
import Replay from '@mui/icons-material/Replay';
import Refresh from '@mui/icons-material/Refresh';

import { useToasterDispatch } from 'common/ui/Toaster/Toaster';
import { HPConfirmationModal } from 'domains/viewer/HangingProtocol/Steps/HPConfirmationModal';
import {
  RESET_ALL_MODAL,
  makeDeleteHPModalContent,
} from 'domains/viewer/HangingProtocol/Steps/stepsModalFactory';
import { RESET_ALL_TOAST } from 'domains/viewer/HangingProtocol/Steps/stepsToastFactory';
import {
  createStepToastContentComponent,
  getDefaultHPToastOptions,
} from 'domains/viewer/HangingProtocol/Steps/StepToast';
import { useClinicalStepOperations } from 'domains/viewer/HangingProtocol/hooks/useClinicalStepOperations';
import { useClinicalViewportEditing } from 'domains/viewer/HangingProtocol/hooks/useClinicalViewportEditing';
import { useHPActionShortcuts } from 'domains/viewer/HangingProtocol/hooks/useHPActionShortcuts';
import { HangingProtocolsForm, type HangingProtocolsFormProps } from './HangingProtocolsForm';
import { HangingProtocolsForm as HangingProtocolsFormV4 } from './HPv4/HangingProtocolsFormV4';
import { useSubmitForm } from './hooks/useSubmitForm';
import {
  POWERED_BY_SIRONA_CLASSIFICATIONS,
  SNACKBAR_CONTENT_PROPS,
  HP_MODAL_VARIANTS,
  HANGING_PROTOCOL_PHASE,
  isAtLeastPhase4,
} from './constants';
import type { WidgetMode, SubmitFormIntent, HangingProtocolPhase } from './constants';
import { getSuccessNotification } from './utils/getSuccessNotification';
import { getHangingProtocolErrorMessage } from './utils/getHangingProtocolErrorMessage';
import { PreviewCell } from './PreviewCell';
import { Row } from './Row';
import { useClearSteppedHPState } from 'domains/viewer/HangingProtocol/hooks/useClearSteppedHPState';
import { makeHPKey } from 'domains/viewer/HangingProtocol/state';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { useDeleteHangingProtocol } from './hooks/useDeleteHangingProtocol';
import { FF, useSplitFlag } from 'modules/feature-flags';
import { Sidebar, useCloseSidebar, useSidebarState } from '../../Sidebar';
import { useBaseViewerData } from '../../StudyLoader/useStudies';
import { useViewerId } from 'hooks/useViewerId';
import { useCurrentCaseContext } from 'common/CurrentCase/CurrentCaseProvider';
import { telemetryContextFor } from 'modules/Apollo/types';
import analytics from 'modules/analytics';
import { viewer } from 'modules/analytics/constants';
import { rootTheme } from 'theme/rootTheme';
import { useNumberOfPriors } from './utils/useNumberOfPriors';
import { getPayloadStudyIds } from './utils/getPayloadStudyIds';

export const HANGING_PROTOCOL_SIDEBAR_ID = 'hanging-protocol';
export const HP_URL_PARAM_KEY = 'hp';
export enum HPUrlParam {
  Create = 'create',
  Edit = 'edit',
}

type HangingProtocolItem = GetAllHangingProtocolsQuery['hangingProtocols']['items'][number];

const menuItemCss = css`
  height: 24px;
  line-height: ${Spacings.xxsmall}rem;
`;

const iconCss = css`
  margin-right: ${Spacings.small}rem;
  color: ${Colors.gray10};
  opacity: 0.5;
  width: 16px;
  height: 16px;
`;

const isSubmitFormIntent = (mode: WidgetMode): mode is SubmitFormIntent =>
  mode === HP_MODAL_VARIANTS.SAVE || mode === HP_MODAL_VARIANTS.UPDATE;

type HangingProtocolWidgetProps = Readonly<{
  layouts: Layouts;
  studyDescriptions: Array<string>;
  viewerRef: {
    current: null | HTMLDivElement;
  };
  onFormOpenChange?: (isFormOpen: boolean) => void;
}>;

const isDefault = (
  hangingProtocol: Nullable<
    GetHangingProtocolMatchQuery['matchHangingProtocolByWorklistsAndStudies']
  >
) => hangingProtocol?.smid == null;

const HangingProtocolWidget = ({
  layouts,
  studyDescriptions,
  viewerRef,
  onFormOpenChange,
}: HangingProtocolWidgetProps): React.ReactElement => {
  const [phase] = useSplitFlag<HangingProtocolPhase>(FF.HANGING_PROTOCOL_PHASE);
  const isPhase4 = isAtLeastPhase4(phase);
  const [searchParams, setSearchParams] = useSearchParams();
  const [widgetMode, setWidgetMode] = useState<WidgetMode>(null);
  const [isOpen, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);

  const { currentCase } = useCurrentCaseContext();
  const primaryStudy = currentCase?.studies[0];
  const numberOfPriors = useNumberOfPriors({ context: APOLLO_QUERY_CONTEXT });

  const groupStudyIds = useMemo(() => getPayloadStudyIds(currentCase), [currentCase]);

  const { hangingProtocol, loadingHangingProtocol, resetToHP } = useHangingProtocol({
    context: APOLLO_QUERY_CONTEXT,
  });
  const { data: allHangingProtocols, loading } = useQuery<
    GetAllHangingProtocolsQuery,
    GetAllHangingProtocolsQueryVariables
  >(GET_ALL_HANGING_PROTOCOLS, {
    // don't fetch HPs if the widget is not open
    skip: !isOpen,
    context: APOLLO_QUERY_CONTEXT,
  });

  const previewedHangingProtocol = usePreviewHangingProtocolConfiguration();
  const selectedHangingProtocolSmid = useSelectedHangingProtocolSmid();
  const {
    previewHangingProtocol,
    confirmPreview,
    selectHangingProtocol,
    clearPreviewState,
    rejectAndRemovePreview,
    refreshSelectedHangingProtocol,
    clearSelectedHangingProtocol,
    loadingPreview,
  } = usePreviewHangingProtocol({ context: APOLLO_QUERY_CONTEXT });

  const closeSidebar = useCloseSidebar();
  const { isOpen: sidebarIsOpen, activeSidebarId } = useSidebarState();
  const viewingViewportClassifications =
    sidebarIsOpen && activeSidebarId === HANGING_PROTOCOL_SIDEBAR_ID;
  const showSidebarOverlay = viewingViewportClassifications && !isPhase4;

  // Track whether the widget was opened via URL param so the effect can close it
  // when the param is removed (e.g. browser back/forward) without interfering with
  // SEARCH/DELETE modes that don't use URL params.
  const openedViaUrlParam = useRef(false);

  // Derive widget state from URL param (e.g. browser back/forward, direct link)
  useEffect(() => {
    const hpParam = searchParams.get(HP_URL_PARAM_KEY);
    if (hpParam === HPUrlParam.Edit) {
      openedViaUrlParam.current = true;
      setWidgetMode(HP_MODAL_VARIANTS.UPDATE);
      setOpen(true);
    } else if (hpParam === HPUrlParam.Create) {
      openedViaUrlParam.current = true;
      setWidgetMode(HP_MODAL_VARIANTS.SAVE);
      setOpen(true);
    } else if (hpParam == null && openedViaUrlParam.current) {
      openedViaUrlParam.current = false;
      setWidgetMode(null);
      setOpen(false);
    }
  }, [searchParams]);

  const open = useCallback(
    (mode: WidgetMode) => {
      setWidgetMode(mode);
      setOpen(true);

      // Add hp param to URL when entering edit/create mode
      const hpParam =
        mode === HP_MODAL_VARIANTS.UPDATE
          ? HPUrlParam.Edit
          : mode === HP_MODAL_VARIANTS.SAVE
            ? HPUrlParam.Create
            : null;

      if (hpParam) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set(HP_URL_PARAM_KEY, hpParam);
            return next;
          },
          { replace: true }
        );
      }
    },
    [setSearchParams]
  );

  const close = useCallback(() => {
    if (!isOpen || primaryStudy == null) {
      return;
    }

    if (phase === HANGING_PROTOCOL_PHASE.THREE || isPhase4) {
      closeSidebar();
    }

    // Remove hp param from URL when closing
    if (searchParams.has(HP_URL_PARAM_KEY)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(HP_URL_PARAM_KEY);
          return next;
        },
        { replace: true }
      );
    }

    setWidgetMode(null);
    setOpen(false);
  }, [closeSidebar, isOpen, phase, isPhase4, primaryStudy, searchParams, setSearchParams]);

  const [completedMessage, setCompletedMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<React.ReactNode>(null);
  const unsetError = useCallback(() => setErrorMessage(null), []);

  const [highlightRow, setHighlightRow] = useState<number | null>(null);
  const columns = useMemo(
    (): ColumnDef<HangingProtocolItem>[] => [
      {
        header: 'Date',
        accessorFn: ({ created }) => toLocaleString(created),
        size: 100,
        maxSize: 100,
        minSize: 100,
      },
      {
        header: 'Name',
        accessorKey: 'name',
        size: 161,
        maxSize: 161,
        minSize: 161,
      },
      {
        header: () => <div>Actions</div>,
        accessorKey: 'actions',
        size: 134,
        maxSize: 134,
        minSize: 134,
        cell: ({ row }) => (
          <PreviewCell
            row={row}
            hangingProtocol={hangingProtocol}
            previewHangingProtocol={previewHangingProtocol}
            setHighlightRow={setHighlightRow}
            setIsPreviewAccepted={setIsPreviewAccepted}
            isLoadingPreview={loadingPreview}
          />
        ),
      },
    ],
    [hangingProtocol, loadingPreview, previewHangingProtocol]
  );

  const searchItems = useMemo(
    () =>
      widgetMode === HP_MODAL_VARIANTS.SEARCH
        ? (allHangingProtocols?.hangingProtocols?.items || []).filter(
            (item): item is typeof item & { created: NonNullable<typeof item.created> } =>
              item.name.toLowerCase().includes(search.trim().toLowerCase()) && item.created != null
          )
        : [],
    [allHangingProtocols?.hangingProtocols?.items, widgetMode, search]
  );

  useOnUnload(rejectAndRemovePreview);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [isPreviewAccepted, setIsPreviewAccepted] = useState(false);

  const deleteHangingProtocol = useDeleteHangingProtocol({ context: APOLLO_QUERY_CONTEXT });
  const clearSteppedHPState = useClearSteppedHPState();
  const currentCaseId = useCurrentCaseId();

  const quickUpdate = useSubmitForm({
    hangingProtocol,
    intent: HP_MODAL_VARIANTS.UPDATE,
    currentCase,
    shouldUpdateClassifications: false,
    numberOfPriors,
    includeLayoutChanges: true,
    layouts,
    studyDescriptions,
    groupedStudies: groupStudyIds,
    context: APOLLO_QUERY_CONTEXT,
    onComplete: (hangingProtocolName) => {
      setCompletedMessage(getSuccessNotification(hangingProtocolName, HP_MODAL_VARIANTS.UPDATE));
    },
    onError: (err) => {
      logger.error(err);
      setErrorMessage(getHangingProtocolErrorMessage(err, HP_MODAL_VARIANTS.UPDATE));
    },
  });

  const { invalidateAllPrecachedStudies, refetch } = useBaseViewerData({
    context: APOLLO_QUERY_CONTEXT,
  });

  const { revertAllStepsToBaseline, flushPendingSaves, flushPendingDeletions } =
    useClinicalStepOperations();
  const clinicalEditing = useClinicalViewportEditing();
  const { enqueueToast } = useToasterDispatch();

  const handleResetAll = useCallback(async () => {
    setShowResetModal(false);
    // Start the pending deferred operations, but hold the await until just before the refetch
    // below. Only the server read has to be ordered behind them -- the local reverts that follow
    // are pure state updates, and awaiting first would stall the whole reset UI on a network
    // round-trip.
    //
    // Starting them here rather than after the reverts is deliberate: revertAllStepsToBaseline
    // cancels anything still pending, so reverting first would leave the flush with nothing to
    // send and drop a save the user had already been told succeeded. Both flushes clear their
    // queues synchronously before their first await, so the revert below is a no-op on them.
    const flushed = Promise.all([flushPendingSaves(), flushPendingDeletions()]);
    revertAllStepsToBaseline();
    clinicalEditing.revertAll();
    // Reload HP from server. The flushed writes have to have landed first, or this read can come
    // back with pre-save data and re-seed the step atoms with it, which is what made an
    // already-saved step reappear as unsaved (VX-4740).
    await flushed;
    await invalidateAllPrecachedStudies();
    invalidateHPInitializationCache();
    invalidateHPSmidGuard();
    await refetch();
    // The cached payload for the selected HP predates the flushed writes, so re-resolve it from the
    // server. Reset All resets *changes*, not the selection -- the selection is an smid and is
    // deliberately left alone here, which is what stops the viewer falling through to the server
    // match and silently putting the original protocol back (VX-4789).
    await refreshSelectedHangingProtocol();
    resetToHP();
    broadcastHPInvalidation();
    enqueueToast(RESET_ALL_TOAST.message, {
      ...getDefaultHPToastOptions(),
      ContentComponent: createStepToastContentComponent({}),
    });
  }, [
    flushPendingSaves,
    flushPendingDeletions,
    revertAllStepsToBaseline,
    clinicalEditing,
    refreshSelectedHangingProtocol,
    invalidateAllPrecachedStudies,
    refetch,
    resetToHP,
    enqueueToast,
  ]);

  // Keyboard equivalents of the dropdown's menu items. Each mirrors its `onClick` exactly, including
  // closing the dropdown, so a hotkey and a click land in the same place -- Reset in particular
  // still routes through the confirmation modal rather than resetting outright.
  const shouldOpenHangingProtocolsForm = isSubmitFormIntent(widgetMode);
  const isHPFormOpen = shouldOpenHangingProtocolsForm && isOpen;

  /**
   * Any of the widget's surfaces being up: the create/edit form, the browse or delete modal, or the Reset All
   * confirmation. Each shortcut below switches mode or resets, so firing one now would swap the surface underneath the
   * reader -- replacing a delete confirmation outright, or opening Browse behind an active Reset All -- and skip the
   * confirm-or-cancel the surface exists to ask for. These are mutually exclusive by construction, so a shortcut
   * declines rather than trying to close one and open another.
   */
  const isHPSurfaceOpen = (widgetMode != null && isOpen) || showResetModal;

  const hpActionShortcutHandlers = useMemo(
    () => ({
      HP_CREATE_NEW: () => {
        if (isHPSurfaceOpen) return false;
        setIsDropdownOpen(false);
        open(HP_MODAL_VARIANTS.SAVE);
      },
      HP_EDIT: () => {
        if (isHPSurfaceOpen) return false;
        setIsDropdownOpen(false);
        open(HP_MODAL_VARIANTS.UPDATE);
      },
      HP_RESET: () => {
        if (isHPSurfaceOpen) return false;
        setIsDropdownOpen(false);
        setShowResetModal(true);
      },
      HP_BROWSE: () => {
        if (isHPSurfaceOpen) return false;
        setIsDropdownOpen(false);
        open(HP_MODAL_VARIANTS.SEARCH);
      },
    }),
    [open, isHPSurfaceOpen]
  );

  useHPActionShortcuts(hpActionShortcutHandlers, { context: APOLLO_QUERY_CONTEXT });

  useEffect(() => {
    onFormOpenChange?.(isHPFormOpen);
  }, [isHPFormOpen, onFormOpenChange]);

  // NOTE: This is implemented to where a users viewports configurations and layout are
  // ALWAYS reset after they submit an HP. This is intentional business logic.
  const onHangingProtocolFormSubmit = useCallback(
    async (hangingProtocolName: string, createdHangingProtocolSmid?: string) => {
      // Only drop the init guard when the HP the viewer should be showing actually changes -- a
      // create, or a switch. On a plain update of the HP already applied, the form has just written
      // the saved steps into the viewer atoms itself, and clearing the guard lets the next
      // getBaseViewerData response re-initialize over that write. The form refetches immediately
      // after the mutation, so if the backend has not made the write visible yet that response is
      // the pre-update HP -- and the freshly added steps disappear from the step bar until a reload
      // (VX-4770). The form deliberately does not clear the guard for exactly this reason; the
      // widget was undoing that a level up.
      if (widgetMode !== HP_MODAL_VARIANTS.UPDATE) {
        invalidateHPInitializationCache();
        invalidateHPSmidGuard();
      }
      clearPreviewState();
      // The submit changed the HP record, so the cached payload for the selected HP is stale.
      // Re-resolve it unconditionally -- it is a cache, and refreshing it cannot change which HP is
      // applied (VX-4809). The selection's smid is what keys useHangingProtocol's init guard, and
      // keeping it stable across the refetch is what stops the response re-initializing the step bar
      // from a different protocol and dropping the steps just added (VX-4770).
      await refreshSelectedHangingProtocol();
      // On CREATE, select the just-created HP so it becomes active immediately, even when the
      // server's post-create refetch ties it with an existing same-criteria HP and keeps
      // auto-matching the older one (VX-4713). Awaited before resetToHP so the reset lands on the
      // new HP's layout, not the stale matched one.
      if (createdHangingProtocolSmid != null) {
        await selectHangingProtocol(createdHangingProtocolSmid);
      }
      resetToHP();
      setCompletedMessage(getSuccessNotification(hangingProtocolName, widgetMode));
      close();
    },
    [
      clearPreviewState,
      refreshSelectedHangingProtocol,
      selectHangingProtocol,
      resetToHP,
      close,
      widgetMode,
    ]
  );

  const confirmPreviewedHangingProtocol = useCallback(() => {
    setIsPreviewAccepted(true);
    logger.info(`Confirmed Hanging Protocol ${previewedHangingProtocol?.smid}`);
    confirmPreview();
    resetToHP();
    setHighlightRow(null);
    close();
  }, [confirmPreview, resetToHP, close, previewedHangingProtocol?.smid]);

  const cancelPreviewedHangingProtocol = useCallback(() => {
    if (!isPreviewAccepted) {
      rejectAndRemovePreview();
      if (previewedHangingProtocol != null) {
        logger.info(`Rejected Hanging Protocol ${previewedHangingProtocol.smid}`);
      }
    }
    setHighlightRow(null);
    close();
  }, [close, isPreviewAccepted, rejectAndRemovePreview, previewedHangingProtocol]);

  const onHangingProtocolFormError = useCallback(
    (err: Error) => {
      logger.error(err);
      setErrorMessage(getHangingProtocolErrorMessage(err, widgetMode as SubmitFormIntent));
    },
    [widgetMode]
  );

  const handleDeleteClick = useCallback(async () => {
    if (hangingProtocol == null || hangingProtocol.smid == null) {
      return;
    }
    // Held in a local so the narrowing survives into the async callbacks below.
    const deletedHPSmid = hangingProtocol.smid;
    // Clear the step state as the delete is ISSUED, not when the server confirms it.
    //
    // This used to live in `onCompleted`, which made the step bar the one part of the delete that was
    // not optimistic -- the mutation already sends an `optimisticResponse`. Measured on the deleting
    // window: `deleteHP()` returned at t=42028 and the clear did not land until t=45428, so the bar
    // advertised a protocol that was already gone for 3.4s, and for longer whenever the response was
    // slow (VX-4859, "until reload"). The atoms are BroadcastChannel-synced, so issuing the clear here
    // also clears the other viewer windows at the same moment.
    //
    // Safe against a failed delete: the HP is refetched on the error path, and the init pass that
    // follows re-applies its steps.
    //
    // The hpKey drops this protocol's persisted unsaved-edit record along with the atoms, so a
    // delete does not leave one stranded on disk to be restored on a later load (VX-4803).
    clearSteppedHPState(makeHPKey(currentCaseId, deletedHPSmid));
    // Tell the other windows at the same moment. The clear alone is not enough: viewer/1 still holds
    // the pre-delete protocol until its own refetch lands, and its re-init publishes that 1-step copy
    // at a winning version, which broadcast-syncs straight back into this window and puts the bar
    // back. Measured: v0 cleared at t=52577 and read false at t=52833, then v1 wrote n=1 and the bar
    // returned ~1s later. Broadcasting the deleted smid here makes v1 mark it deleted before it can
    // publish, so the stale response is dropped instead of winning (VX-4859).
    //
    // The post-confirmation broadcast below stays: it is what triggers the other windows' refetch.
    broadcastHPInvalidation({ deletedHPSmid });
    await deleteHangingProtocol({
      variables: { id: deletedHPSmid },
      optimisticResponse: {
        __typename: 'Mutation',
        deleteHangingProtocol: {
          __typename: 'HangingProtocolDeleted',
          smid: hangingProtocol.smid,
        },
      },
      onCompleted: async (data) => {
        if (hangingProtocol?.name == null) {
          return;
        }

        if (isPhase4 && hangingProtocol) {
          analytics.track(viewer.usr.hangingProtocolDeleted, {
            hanging_protocol_name: hangingProtocol.name,
            hanging_protocol_smid: hangingProtocol.smid,
            intent: 'delete',
            hanging_protocol_payload: {
              name: hangingProtocol.name,
              study_descriptions: hangingProtocol.studyDescriptions ?? [],
              number_of_priors: hangingProtocol.numberOfPriors ?? 0,
              study_criteria: hangingProtocol.studyCriteria ?? null,
              layouts: hangingProtocol.data?.layouts ?? [],
              viewports_configurations:
                hangingProtocol.viewportsConfigurations?.map((vc) => ({
                  id: vc.id,
                  view_type: vc.configuration.viewType,
                  series_descriptions: vc.configuration.seriesDescriptions ?? [],
                  series_criteria: vc.configuration.seriesCriteria ?? null,
                  comparison_index: vc.configuration.comparisonIndex,
                })) ?? [],
            },
          });
        }

        setCompletedMessage(`Hanging protocol '${hangingProtocol.name}' deleted successfully.`);
        close();
        // The step state, including this protocol's persisted unsaved-edit record, was already
        // cleared when the delete was issued, above (VX-4859 moved it there; VX-4803 added the key).
        // Clearing the atoms is not enough on its own: this window's own in-flight and background
        // queries can still resolve with the deleted HP after the init guard is cleared below, and
        // re-populate the step bar from it. Marking the smid deleted makes those responses be
        // dropped (VX-4738).
        markHangingProtocolDeleted(deletedHPSmid);
        await invalidateAllPrecachedStudies();
        invalidateHPInitializationCache();
        // Deleting the active HP is a legitimate transition to null/default -- clear the
        // smid-write guard so it isn't mistaken for a transient refetch blip and skipped,
        // which would leave activeHangingProtocolSmidState pointing at the deleted HP (VX-4615
        // review).
        invalidateHPSmidGuard();
        await refetch();
        // Signal other viewer windows to refetch so they pick up the deletion. They need the smid
        // too -- each window has its own query lifecycle and its own late responses to drop.
        broadcastHPInvalidation({ deletedHPSmid });
      },
      update(cache, { data }) {
        if (data == null) {
          logger.warn('Unable to delete hanging protocol, missing update data argument');
          return;
        }

        // Reject preview if the deleted hanging protocol matches the deleted one
        if (data.deleteHangingProtocol?.smid === previewedHangingProtocol?.smid) {
          rejectAndRemovePreview();
        }

        // A selected HP is no longer backed by a real record once deleted — forget the selection
        // (not just its cached payload) so originalHangingProtocol falls back to the (refetched)
        // server-matched HP instead of keeping the deleted one applied indefinitely.
        if (data.deleteHangingProtocol?.smid === selectedHangingProtocolSmid) {
          clearSelectedHangingProtocol();
        }
      },
    });
  }, [
    clearSteppedHPState,
    currentCaseId,
    close,
    deleteHangingProtocol,
    hangingProtocol,
    invalidateAllPrecachedStudies,
    refetch,
    isPhase4,
    previewedHangingProtocol?.smid,
    rejectAndRemovePreview,
    selectedHangingProtocolSmid,
    clearSelectedHangingProtocol,
  ]);

  // Type annotation ensures properties aren't inferred as optional.
  // Conditional ensures `widgetMode` is narrowed to `SubmitFormIntent`.
  const hangingProtocolsFormProps: HangingProtocolsFormProps | null = isSubmitFormIntent(widgetMode)
    ? {
        hangingProtocol,
        onSubmit: onHangingProtocolFormSubmit,
        onError: onHangingProtocolFormError,
        onClose: close,
        layouts,
        studyDescriptions,
        groupedStudies: groupStudyIds,
        mode: widgetMode,
        phase,
      }
    : null;

  return (
    <>
      <Tooltip content={hangingProtocol && !isHPFormOpen ? 'Hanging Protocol' : null}>
        <Menu
          disabled={isHPFormOpen}
          placement="bottom-end"
          visible={isDropdownOpen}
          onClickOutside={() => {
            setIsDropdownOpen(false);
            close();
          }}
          css={css`
            margin-top: ${Spacings.xsmall}rem;
            margin-bottom: ${Spacings.xsmall}rem;
            opacity: 0.7;
          `}
          appendTo={() => document.body}
          data-testid="hanging-protocol-menu"
          render={() => (
            <>
              <MenuItem css={menuItemCss} onClick={() => {}} disabled>
                <Text color="secondary">Hanging protocols</Text>
              </MenuItem>
              <MenuItem
                title="Browse hanging protocols"
                css={menuItemCss}
                onClick={() => {
                  setIsDropdownOpen(false);
                  open(HP_MODAL_VARIANTS.SEARCH);
                }}
                data-testid="browse-hanging-protocols"
                data-analytics-name="hanging-protocol-browse-button"
              >
                <SearchOutlined css={iconCss} fontSize="small" />
                <Text>Browse</Text>
              </MenuItem>
              <MenuItem
                title="Create new hanging protocol from current viewports"
                css={menuItemCss}
                onClick={() => {
                  setIsDropdownOpen(false);
                  open(HP_MODAL_VARIANTS.SAVE);
                }}
                data-testid="create-hanging-protocols"
                data-analytics-name="hanging-protocol-create-button"
              >
                <AddCircle css={iconCss} fontSize="small" />

                <Text>Create</Text>
              </MenuItem>
              <MenuItem
                title="Edit hanging protocol"
                css={menuItemCss}
                onClick={() => {
                  setIsDropdownOpen(false);
                  open(HP_MODAL_VARIANTS.UPDATE);
                }}
                data-testid="update"
                data-analytics-name="hanging-protocol-update-button"
              >
                <BorderColor css={iconCss} fontSize="small" />
                Edit
              </MenuItem>
              <MenuItem
                title="Reset viewports to hanging protocol defaults"
                css={menuItemCss}
                onClick={() => {
                  setIsDropdownOpen(false);
                  setShowResetModal(true);
                }}
                data-testid="reset-hanging-protocol"
                data-analytics-name="hanging-protocol-reset-button"
              >
                <Refresh css={iconCss} fontSize="small" />
                Reset All
              </MenuItem>
              {!isPhase4 && (
                <MenuItem
                  title="Only update hanging protocol layout from current configuration"
                  css={menuItemCss}
                  onClick={(evt) => {
                    setIsDropdownOpen(false);
                    quickUpdate(evt);
                  }}
                  data-testid="update-layout-hanging-protocols"
                  data-analytics-name="hanging-protocol-update-layout-button"
                >
                  <Replay css={iconCss} fontSize="small" />
                  Update layout only
                </MenuItem>
              )}
              <MenuItem
                title="Delete hanging protocol"
                onClick={() => {
                  setIsDropdownOpen(false);
                  open(HP_MODAL_VARIANTS.DELETE);
                }}
                data-testid="delete"
                css={menuItemCss}
              >
                <DeleteForeverIcon css={iconCss} fontSize="small" />
                Delete
              </MenuItem>
            </>
          )}
        >
          <BaseWidget
            data-testid="hanging-protocol-widget"
            placeholder="Hanging Protocol"
            text={!loadingHangingProtocol ? hangingProtocol?.name : 'Loading...'}
            disabled={isHPFormOpen}
            onClick={() => {
              if (isDropdownOpen) {
                setIsDropdownOpen(false);
              } else {
                open(HP_MODAL_VARIANTS.SEARCH);
              }
            }}
            accessories={
              <>
                <AccessoryButton
                  onClick={() => open(HP_MODAL_VARIANTS.SEARCH)}
                  data-testid="hanging-protocol-search"
                  disabled={isHPFormOpen}
                  icon={<SearchIcon />}
                />
                {!isDefault(hangingProtocol) && (
                  <AccessoryButton
                    icon={<MoreVert />}
                    data-testid="hanging-protocol-dropdown"
                    disabled={isHPFormOpen}
                    onClick={(evt) => {
                      evt.stopPropagation();
                      setIsDropdownOpen((open) => !open);
                    }}
                  />
                )}
                {isDefault(hangingProtocol) && (
                  <Tooltip
                    content={
                      <>
                        Create new hanging protocol
                        <br />
                        from current viewports
                      </>
                    }
                  >
                    <AccessoryButton
                      onClick={(evt) => {
                        evt.stopPropagation();
                        open(HP_MODAL_VARIANTS.SAVE);
                      }}
                      data-testid="hanging-protocol-save-button"
                      data-analytics-name="hanging-protocol-save-button"
                      disabled={isHPFormOpen}
                      icon={<AddCircle />}
                    />
                  </Tooltip>
                )}
              </>
            }
          />
        </Menu>
      </Tooltip>
      {isPhase4 ? (
        <Sidebar
          activeSidebarId={HANGING_PROTOCOL_SIDEBAR_ID}
          isOpen={shouldOpenHangingProtocolsForm && isOpen}
          showOverlay={showSidebarOverlay}
        >
          {hangingProtocolsFormProps != null && (
            <HangingProtocolsFormV4 className="MuiDialog-paper" {...hangingProtocolsFormProps} />
          )}
        </Sidebar>
      ) : phase === HANGING_PROTOCOL_PHASE.THREE ? (
        <Sidebar
          activeSidebarId={HANGING_PROTOCOL_SIDEBAR_ID}
          isOpen={shouldOpenHangingProtocolsForm && isOpen}
          showOverlay={showSidebarOverlay}
        >
          {hangingProtocolsFormProps != null && (
            <HangingProtocolsForm className="MuiDialog-paper" {...hangingProtocolsFormProps} />
          )}
        </Sidebar>
      ) : (
        <Dialog open={shouldOpenHangingProtocolsForm && isOpen} onClose={close}>
          {hangingProtocolsFormProps != null && (
            <HangingProtocolsForm {...hangingProtocolsFormProps} />
          )}
        </Dialog>
      )}
      {widgetMode === HP_MODAL_VARIANTS.DELETE && isOpen && (
        <HPConfirmationModal
          {...makeDeleteHPModalContent(hangingProtocol?.name ?? '')}
          // The e2e page objects have always driven deletion by this id; keeping it means this swap is invisible to
          // them.
          primaryActionTestId="delete-hanging-protocol-button"
          onPrimaryAction={handleDeleteClick}
          onSecondaryAction={close}
          onClose={close}
        />
      )}
      {showResetModal && (
        <HPConfirmationModal
          {...RESET_ALL_MODAL}
          onPrimaryAction={handleResetAll}
          onSecondaryAction={() => setShowResetModal(false)}
          onClose={() => setShowResetModal(false)}
        />
      )}
      {isOpen && widgetMode === HP_MODAL_VARIANTS.SEARCH && (
        <DraggableContainer
          dragConstraintRef={viewerRef}
          css={css`
            margin-top: 3rem;
          `}
        >
          <Stack
            css={`
              padding: 12px 32px;
              min-width: 458px;
              min-height: 501px;
              border-radius: ${rootTheme.radius.sm}px;
              background-color: ${Colors.gray3};
              color: ${Colors.gray8};
            `}
            data-testid="search-hanging-protocol-modal"
          >
            <Stack
              css={`
                display: flex;
                flex-direction: column;
                justify-content: space-between;
              `}
            >
              <Text variant="display1">Select Hanging Protocol</Text>
              <Text
                css={`
                  padding: 0;
                `}
              >
                {POWERED_BY_SIRONA_CLASSIFICATIONS}
              </Text>
              <Input
                icon={<SearchIcon />}
                placeholder="Enter search term"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                type="search"
                autoFocus={true}
                css={`
                  margin-top: 18px;
                `}
              />

              <Table
                data={searchItems}
                columns={columns}
                Row={Row}
                // @ts-expect-error Table expects boolean but Row component uses number for index comparison
                highlightRow={highlightRow}
                showHeaderSeparators={true}
                loading={loading}
                css={`
                  background-color: ${Colors.gray1};
                  border-radius: ${rootTheme.radius.sm}px;
                  margin: 18px 0 6px 0;
                  flex: 1;
                  min-height: 0;
                  padding: 10px;
                  max-height: 264px;
                  min-height: 264px;
                `}
              />

              <Text variant="body2">
                Click confirm to keep or cancel to revert to previous settings.
              </Text>

              <Stack css="margin: 24px 0 35px 0; justify-content: flex-end; gap: 18px;">
                <Button
                  onClick={cancelPreviewedHangingProtocol}
                  variant="ghost"
                  data-testid="cancel-previewed-hanging-protocol"
                  disabled={loadingPreview}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="confirm-previewed-hanging-protocol"
                  onClick={confirmPreviewedHangingProtocol}
                  variant="primary"
                  disabled={previewedHangingProtocol == null || loadingPreview}
                >
                  Confirm
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </DraggableContainer>
      )}
      <GenericSnackBar
        open={completedMessage != null}
        msg={completedMessage ?? ''}
        handleClose={() => setCompletedMessage(null)}
        severity="default"
        data-testid="success-hanging-protocol"
        ContentProps={SNACKBAR_CONTENT_PROPS}
        icon={completedMessage?.includes('delete') ? 'redDeleteForever' : 'greenCheck'}
      />
      <GenericSnackBar
        open={errorMessage != null}
        msg={errorMessage ?? ''}
        handleClose={unsetError}
        severity="default"
        icon="redWarning"
        data-testid="error-hanging-protocol"
        ContentProps={SNACKBAR_CONTENT_PROPS}
      />
    </>
  );
};

const MemoHangingProtocolWidget = memo(HangingProtocolWidget);

export default MemoHangingProtocolWidget;

export function useShouldShowHangingProtocolWidget() {
  const viewerId = useViewerId();
  return viewerId === '0';
}

HangingProtocolWidget.displayName = 'HangingProtocolWidget';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(HangingProtocolWidget);
